import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase/firebase";
import {
  doc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";

function AddQRCode() {
  const [currentUser, setCurrentUser] = useState(null);
  const [qrLink, setQrLink] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [status, setStatus] = useState(""); // "", "checking", "available", "taken"
  const [error, setError] = useState("");
  const nameInputRef = useRef(null);
  const qrCodeScannerRef = useRef(null);
  const scannerInitializedRef = useRef(false); // ✅ NEU
  const navigate = useNavigate();

  // Auth Check
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate("/login");
      } else {
        setCurrentUser(user);
      }
    });
    return () => unsub();
  }, [navigate]);

  // Kamera automatisch stoppen bei Verlassen
  useEffect(() => {
    return () => {
      if (qrCodeScannerRef.current) {
        const state = qrCodeScannerRef.current.getState();
        if (state === Html5QrcodeScannerState.SCANNING) {
          qrCodeScannerRef.current.stop().then(() => {
            document.getElementById("qr-reader").innerHTML = "";
            qrCodeScannerRef.current = null;
            scannerInitializedRef.current = false;
          });
        }
      }
    };
  }, []);

  // Fokus auf Name setzen wenn verfügbar
  useEffect(() => {
    if (status === "available" && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [status]);

  // Kamera beim Laden automatisch starten
  useEffect(() => {
    startQrScan();
  }, []);

  const startQrScan = async () => {
    const qrRegionId = "qr-reader";

    // ✅ Doppelte Initialisierung verhindern
    if (scannerInitializedRef.current) return;
    scannerInitializedRef.current = true;

    document.getElementById(qrRegionId).innerHTML = "";

    try {
      const html5QrCode = new Html5Qrcode(qrRegionId);
      qrCodeScannerRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          if (!decodedText.startsWith("https://my.qr-shirt.de/")) return;

          setQrLink(decodedText);
          setStatus("checking");

          const match = decodedText.match(/^https:\/\/my\.qr-shirt\.de\/([\w-]+)$/);
          const qrCodeId = match?.[1];

          if (!qrCodeId) {
            setError("Ungültiger QR-Code-Link");
            setStatus("");
            return;
          }

          const pagesRef = collection(db, "pages");
          const q = query(pagesRef, where("link", "==", qrCodeId));
          const snapshot = await getDocs(q);

          if (!snapshot.empty) {
            setStatus("taken");
            return;
          }

          setStatus("available");

          const state = html5QrCode.getState();
          if (state === Html5QrcodeScannerState.SCANNING) {
            await html5QrCode.stop();
            document.getElementById(qrRegionId).innerHTML = "";
            qrCodeScannerRef.current = null;
            scannerInitializedRef.current = false;
          }
        },
        () => {}
      );
    } catch (err) {
      setError("Fehler beim Starten der Kamera: " + err.message);
      scannerInitializedRef.current = false;
    }
  };

  const handleAddQRCode = async () => {
    try {
      const match = qrLink.match(/^https:\/\/my\.qr-shirt\.de\/([\w-]+)$/);
      const qrCodeId = match[1];

      const newPageRef = await addDoc(collection(db, "pages"), {
        link: qrCodeId,
        ownerId: currentUser.uid,
        name: nameInput.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        content: {
          text: "Text noch nicht gesetzt",
          url: "https://example.com",
          image: "https://example.com/default.jpg",
        },
      });

      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, {
        pages: arrayUnion(newPageRef.id),
      });

      navigate("/dashboard");
    } catch (err) {
      setError("Fehler beim Hinzufügen des QR-Codes: " + err.message);
    }
  };

  return (
    <div className="container mt-5" style={{ maxWidth: "600px" }}>
      <h2 className="mb-4 text-center">QR-Code hinzufügen</h2>

      {/* Kamerabereich */}
      {status !== "available" && (
        <div id="qr-reader" style={{ width: "100%" }} className="mb-3"></div>
      )}

      {/* Gefundener Link anzeigen */}
      {qrLink && (
        <div className="mb-3">
          <label className="form-label fw-semibold">Dein Link:</label>
          <div className="form-control bg-light">{qrLink}</div>
        </div>
      )}

      {/* Vergeben Hinweis */}
      {status === "taken" && (
        <div className="alert alert-danger mt-3">⚠️ QR-Code ist bereits vergeben.</div>
      )}

      {/* Eingabemaske für Name */}
      {status === "available" && (
        <div className="mt-4">
          <div className="mb-3">
            <label htmlFor="qrName" className="form-label">Name des Kleidungsstücks</label>
            <input
              type="text"
              className="form-control"
              id="qrName"
              placeholder="z. B. Mein Hoodie"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              ref={nameInputRef}
            />
          </div>

          <div className="d-grid gap-2">
            <button
              className="btn btn-success"
              onClick={handleAddQRCode}
              disabled={!nameInput.trim()}
            >
              QR-Code speichern
            </button>
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger mt-3">{error}</div>}
    </div>
  );
}

export default AddQRCode;
